import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { getAxiosInstance } from '../../../axiosInstances/axios';
import { getPrivateAxiosInstance } from '../../../axiosInstances/axiosPrivate';
import { AcademiaResponse } from '../../../types/common';
import { AllLanguage, Language } from '../../../types/languages';
import { returnQueryConfig } from '../../../utils/commonFunctions';

const useLanguageActions = (
  whichAction: 'allLanguages' | 'addedLanguages' | ''
) => {
  // query function to fetch all the languages from the database declared here
  const getAllLanguagesHandler = useQuery({
    queryKey: ['all-languages'],
    queryFn: async (): Promise<AcademiaResponse<Array<AllLanguage>>> => {
      const response = await getPrivateAxiosInstance('user').get(
        '/all/language/'
      );

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'allLanguages'),
    staleTime: Infinity,
    gcTime: Infinity,
  });

  // query function to fetch all the added languages from the database declared here
  const getAddedLanguagesHandler = useQuery({
    queryKey: ['added-languages'],
    queryFn: async (): Promise<AcademiaResponse<Array<Language>>> => {
      const response = await getAxiosInstance('user').get('/language/list/');

      return response.data;
    },

    ...returnQueryConfig(whichAction === 'addedLanguages'),
  });

  // mutation function to add new language(s) in the database declared here
  const addNewLanguagesHandler = useMutation({
    mutationFn: async ({
      body,
    }: {
      body: Array<AllLanguage>;
    }): Promise<AcademiaResponse<Array<Language>>> => {
      const response = await getPrivateAxiosInstance('user').post(
        '/language/create/',
        body
      );

      return response.data;
    },
  });

  // mutation function to delete existing language from the database declared here
  const deleteLanguageByIdHandler = useMutation({
    mutationFn: ({
      langId,
    }: {
      langId: number;
    }): Promise<AxiosResponse<AcademiaResponse<null>>> =>
      getPrivateAxiosInstance('user').delete(`/language/${langId}/delete/`),
  });

  return {
    getAllLanguagesHandler,
    getAddedLanguagesHandler,
    addNewLanguagesHandler,
    deleteLanguageByIdHandler,
  };
};

export default useLanguageActions;
